<template>
  <div class="auth-page">
    <div class="scene-shade" aria-hidden="true"></div>

    <div class="auth-shell">
      <section class="auth-copy reveal-up" aria-labelledby="auth-title">
        <div class="brand-lockup">
          <img
            class="brand-mark-image"
            src="/img/robocoin-logo-mark.215eaf9d.png"
            alt=""
            aria-hidden="true"
          />
          <span>
            <strong>RoboCoin DeRAS</strong>
            <small>Global Developer &amp; Agent Portal</small>
          </span>
        </div>

        <p class="eyebrow">GLOBAL DEVELOPER &amp; AGENT PORTAL</p>
        <h1 id="auth-title">Build the Autonomous Machine Economy</h1>
        <p class="lead">
          Register machine agents, install the ROBO SDK, dispatch verifiable missions, and connect
          physical machines to the global DeRAS network.
        </p>
        <div class="ecosystem-prompt">
          <span class="live-dot" aria-hidden="true"></span>
          <span>Join the ROBO Ecosystem</span>
        </div>
        <div class="hero-actions">
          <button class="hero-btn hero-btn-primary" type="button" @click="showRegister">
            Create Developer Account
          </button>
          <button class="hero-btn hero-btn-secondary" type="button" @click="openSdkDocumentation">
            View SDK Documentation
          </button>
        </div>

        <section class="mobile-access" aria-labelledby="mobile-access-title">
          <div class="mobile-qr-box">
            <img v-if="mobileQrDataUrl" :src="mobileQrDataUrl" alt="RoboCoin mobile H5 access QR code" />
            <el-icon v-else><Loading /></el-icon>
          </div>
          <div class="mobile-access-copy">
            <span>Mobile H5 Access</span>
            <h2 id="mobile-access-title">Scan to continue on your phone</h2>
            <p>{{ mobilePortalHint }}</p>
            <div class="mobile-access-actions">
              <button type="button" @click="openMobilePortal">
                <el-icon><Cellphone /></el-icon>
                <span>Open H5</span>
              </button>
              <button type="button" title="Copy mobile H5 URL" @click="copyMobilePortalUrl">
                <el-icon><CopyDocument /></el-icon>
                <span>Copy link</span>
              </button>
            </div>
          </div>
        </section>
      </section>

      <section class="auth-panel reveal-up delay-1">
        <div class="panel-topbar">
          <div class="panel-brand">
            <img
              class="panel-brand-image"
              src="/img/robocoin-logo-mark.215eaf9d.png"
              alt=""
              aria-hidden="true"
            />
            <span>RoboCoin DeRAS</span>
          </div>
          <button class="home-link" type="button" @click="goHome" aria-label="Return to home">
            Home
          </button>
        </div>

        <div class="panel-heading">
          <p>{{ activeTab === 'login' ? 'Welcome back' : 'Developer onboarding' }}</p>
          <h2>{{ activeTab === 'login' ? 'Sign in to DeRAS' : 'Create your account' }}</h2>
        </div>

        <div class="auth-switch" role="tablist" aria-label="Account access">
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'login'"
            :class="{ active: activeTab === 'login' }"
            @click="activeTab = 'login'"
          >
            Sign In
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'register'"
            :class="{ active: activeTab === 'register' }"
            @click="activeTab = 'register'"
          >
            Register
          </button>
        </div>

        <div class="auth-form-wrap">
          <div v-show="activeTab === 'login'">
            <el-form
              ref="loginFormRef"
              :model="loginForm"
              :rules="loginRules"
              label-position="top"
              class="auth-form"
            >
              <el-form-item label="Email or international phone" prop="account">
                <el-input
                  v-model.trim="loginForm.account"
                  name="username"
                  autocomplete="username"
                  clearable
                  placeholder="Email or phone with country code"
                  @keyup.enter="submitLogin"
                ></el-input>
              </el-form-item>

              <el-form-item label="Password" prop="password">
                <div class="password-control">
                  <input
                  v-model="loginForm.password"
                    :type="loginPasswordVisible ? 'text' : 'password'"
                  name="password"
                  autocomplete="current-password"
                  placeholder="Enter your password"
                  @keyup.enter="submitLogin"
                  />
                  <button
                    type="button"
                    :aria-label="loginPasswordVisible ? 'Hide password' : 'Show password'"
                    :title="loginPasswordVisible ? 'Hide password' : 'Show password'"
                    @click="loginPasswordVisible = !loginPasswordVisible"
                  >
                    <el-icon><Hide v-if="loginPasswordVisible" /><View v-else /></el-icon>
                  </button>
                </div>
              </el-form-item>

              <button class="primary-btn" type="button" :disabled="submitting" @click="submitLogin">
                {{ submitting && activeTab === 'login' ? 'Signing in...' : 'Sign In' }}
              </button>
            </el-form>
          </div>

          <div v-show="activeTab === 'register'">
            <el-form
              ref="registerFormRef"
              :model="registerForm"
              :rules="registerRules"
              label-position="top"
              class="auth-form"
            >
              <el-form-item label="Display name">
                <el-input
                  v-model.trim="registerForm.username"
                  name="nickname"
                  maxlength="100"
                  placeholder="Optional; generated if left blank"
                ></el-input>
              </el-form-item>

              <el-form-item label="Email" prop="email">
                <el-input
                  v-model.trim="registerForm.email"
                  name="email"
                  autocomplete="email"
                  placeholder="name@example.com"
                ></el-input>
              </el-form-item>

              <div class="phone-grid">
                <el-form-item label="Country code" prop="phoneCountryCode">
                  <el-input
                    v-model.trim="registerForm.phoneCountryCode"
                    placeholder="+86 / +49 / +1"
                  ></el-input>
                </el-form-item>

                <el-form-item label="Phone number" prop="phone">
                  <el-input
                    v-model.trim="registerForm.phone"
                    placeholder="Local phone number"
                  ></el-input>
                </el-form-item>
              </div>

              <p class="field-tip">Provide an email or a phone number. Phone numbers require a country code.</p>

              <el-form-item label="Password" prop="password">
                <div class="password-control">
                  <input
                  v-model="registerForm.password"
                    :type="registerPasswordVisible ? 'text' : 'password'"
                  name="new-password"
                  autocomplete="new-password"
                  placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    :aria-label="registerPasswordVisible ? 'Hide password' : 'Show password'"
                    :title="registerPasswordVisible ? 'Hide password' : 'Show password'"
                    @click="registerPasswordVisible = !registerPasswordVisible"
                  >
                    <el-icon><Hide v-if="registerPasswordVisible" /><View v-else /></el-icon>
                  </button>
                </div>
              </el-form-item>

              <el-form-item label="Confirm password" prop="confirmPassword">
                <div class="password-control">
                  <input
                  v-model="registerForm.confirmPassword"
                    :type="confirmPasswordVisible ? 'text' : 'password'"
                  name="confirm-password"
                  autocomplete="new-password"
                  placeholder="Enter your password again"
                  />
                  <button
                    type="button"
                    :aria-label="confirmPasswordVisible ? 'Hide password' : 'Show password'"
                    :title="confirmPasswordVisible ? 'Hide password' : 'Show password'"
                    @click="confirmPasswordVisible = !confirmPasswordVisible"
                  >
                    <el-icon><Hide v-if="confirmPasswordVisible" /><View v-else /></el-icon>
                  </button>
                </div>
              </el-form-item>

              <button class="primary-btn warm-btn" type="button" :disabled="submitting" @click="submitRegister">
                {{ submitting && activeTab === 'register' ? 'Creating account...' : 'Create Developer Account' }}
              </button>
            </el-form>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Cellphone, CopyDocument, Hide, Loading, View } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import QRCode from 'qrcode'
import { loginUser, registerUser } from '../api/auth'
import { getApiErrorMessage } from '../api/http'
import { setAuthRole, setAuthUser, setToken } from '../utils/auth'

const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i
const countryCodePattern = /^\+[1-9]\d{0,3}$/
const localPhonePattern = /^[0-9()\-\s]{4,20}$/
const internationalPhonePattern = /^\+[1-9]\d{6,14}$/

export default {
  name: 'AuthPage',
  components: {
    Cellphone,
    CopyDocument,
    Hide,
    Loading,
    View
  },
  data() {
    const validateLoginAccount = (rule, value, callback) => {
      if (!value) {
        callback(new Error('Enter an email or international phone number'))
        return
      }

      const account = value.trim()
      const normalizedPhone = this.normalizeLoginPhone(account)
      if (emailPattern.test(account) || internationalPhonePattern.test(normalizedPhone || '')) {
        callback()
        return
      }

      callback(new Error('Enter a valid email or phone number with a country code'))
    }

    const validateRegisterIdentity = (rule, value, callback) => {
      const email = (this.registerForm.email || '').trim()
      const countryCode = (this.registerForm.phoneCountryCode || '').trim()
      const phone = (this.registerForm.phone || '').trim()

      if (!email && !phone && !countryCode) {
        callback(new Error('Provide either an email or a phone number'))
        return
      }

      if (email && !emailPattern.test(email)) {
        callback(new Error('Enter a valid email address'))
        return
      }

      if (countryCode || phone) {
        if (!countryCodePattern.test(countryCode)) {
          callback(new Error('Enter a valid country code, such as +86'))
          return
        }
        if (!localPhonePattern.test(phone)) {
          callback(new Error('Enter a valid phone number'))
          return
        }
      }

      callback()
    }

    const validatePassword = (rule, value, callback) => {
      if (!value || value.length < 8) {
        callback(new Error('Password must contain at least 8 characters'))
        return
      }
      callback()
    }

    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.registerForm.password) {
        callback(new Error('Passwords do not match'))
        return
      }
      callback()
    }

    return {
      activeTab: 'login',
      submitting: false,
      mobileQrDataUrl: '',
      mobileQrError: '',
      loginPasswordVisible: false,
      registerPasswordVisible: false,
      confirmPasswordVisible: false,
      loginForm: {
        account: '',
        password: ''
      },
      registerForm: {
        username: '',
        email: '',
        phoneCountryCode: '',
        phone: '',
        password: '',
        confirmPassword: ''
      },
      loginRules: {
        account: [{ validator: validateLoginAccount, trigger: 'blur' }],
        password: [{ required: true, message: 'Enter your password', trigger: 'blur' }]
      },
      registerRules: {
        email: [{ validator: validateRegisterIdentity, trigger: 'blur' }],
        phoneCountryCode: [{ validator: validateRegisterIdentity, trigger: 'blur' }],
        phone: [{ validator: validateRegisterIdentity, trigger: 'blur' }],
        password: [{ validator: validatePassword, trigger: 'blur' }],
        confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
      }
    }
  },
  computed: {
    mobilePortalUrl() {
      const configured = String(process.env.VUE_APP_MOBILE_PORTAL_URL || '').trim()
      if (configured) {
        return configured.includes('#/')
          ? configured
          : `${configured.replace(/\/$/, '')}/#/mobile`
      }
      return `${window.location.origin}${window.location.pathname.replace(/\/$/, '')}/#/mobile`
    },
    mobilePortalHint() {
      if (this.mobileQrError) return 'QR code is temporarily unavailable. Use Copy link instead.'
      if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        return 'Open this page with its LAN address before scanning on a phone.'
      }
      return 'Use Chrome, Edge, or your phone camera to open the mobile portal.'
    }
  },
  mounted() {
    this.renderMobileQrCode()
  },
  methods: {
    async renderMobileQrCode() {
      try {
        this.mobileQrDataUrl = await QRCode.toDataURL(this.mobilePortalUrl, {
          width: 180,
          margin: 2,
          errorCorrectionLevel: 'M',
          color: {
            dark: '#101820',
            light: '#ffffff'
          }
        })
      } catch (error) {
        this.mobileQrError = error && error.message ? error.message : 'QR generation failed'
      }
    },
    openMobilePortal() {
      this.$router.push({ name: 'mobilePortal' })
    },
    async copyMobilePortalUrl() {
      const fallbackCopy = () => {
        const input = document.createElement('textarea')
        input.value = this.mobilePortalUrl
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.mobilePortalUrl)
        } else {
          fallbackCopy()
        }
        ElMessage.success('Mobile H5 link copied.')
      } catch (error) {
        fallbackCopy()
        ElMessage.success('Mobile H5 link copied.')
      }
    },
    showRegister() {
      this.activeTab = 'register'
      this.$nextTick(() => {
        const panel = this.$el && this.$el.querySelector('.auth-panel')
        if (panel && window.innerWidth <= 760) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    openSdkDocumentation() {
      this.$router.push({ name: 'openApiDoc' })
    },
    goHome() {
      this.$router.push({ name: 'home' })
    },
    localizeAuthError(message, fallback) {
      const text = String(message || '').trim()
      if (!text) return fallback
      if (/^[\x00-\x7F\s]+$/.test(text)) return text

      const translations = [
        [/账号或密码|密码错误|登录失败/, 'Invalid account or password.'],
        [/用户不存在|账号不存在/, 'Account not found.'],
        [/邮箱.*已.*注册|邮箱.*存在/, 'This email is already registered.'],
        [/手机号.*已.*注册|手机号.*存在/, 'This phone number is already registered.'],
        [/参数|格式/, 'Some account details are invalid.'],
        [/网络|连接|服务/, 'The service is temporarily unavailable. Please try again.']
      ]
      const match = translations.find(([pattern]) => pattern.test(text))
      return match ? match[1] : fallback
    },
    normalizeLoginPhone(account) {
      return String(account || '').replace(/\s+/g, '').replace(/[-()]/g, '').replace(/^00/, '+')
    },
    finishAuth(payload, role = 'user') {
      const data = payload && payload.data ? payload.data : {}
      setToken(data.token || `${role}-token-${Date.now()}`)
      setAuthUser(data.user || { username: this.loginForm.account, role })
      setAuthRole(role)

      const redirect = this.$route.query.redirect
      const allowList = ['mall', 'user', 'taskMarket', 'activeOrders', 'profile', 'mobileTaskPublish']
      const targetName = redirect && allowList.includes(redirect) ? redirect : 'home'
      ElMessage.success('Signed in successfully.')
      this.$router.replace({ name: targetName }).catch(() => {
        window.location.assign(this.$router.resolve({ name: targetName }).href)
      })
    },
    async validateForm(refName) {
      const form = this.$refs[refName]
      if (!form) return false
      try {
        await form.validate()
        return true
      } catch (error) {
        return false
      }
    },
    async submitLogin() {
      const valid = await this.validateForm('loginFormRef')
      if (!valid) {
        ElMessage.warning('Please complete the highlighted sign-in fields.')
        return
      }

      this.submitting = true
      try {
        const response = await loginUser(this.loginForm)
        if (response.data.code !== 200) {
          ElMessage.error(this.localizeAuthError(response.data.msg, 'Unable to sign in. Check your credentials and try again.'))
          return
        }
        this.finishAuth(response.data, 'user')
      } catch (error) {
        const message = getApiErrorMessage(error, 'Unable to reach the service. Please try again.')
        ElMessage.error(this.localizeAuthError(message, 'Unable to reach the service. Please try again.'))
      } finally {
        this.submitting = false
      }
    },
    async submitRegister() {
      const valid = await this.validateForm('registerFormRef')
      if (!valid) return

      this.submitting = true
      try {
        const payload = {
          username: this.registerForm.username,
          email: this.registerForm.email,
          phoneCountryCode: this.registerForm.phoneCountryCode,
          phone: this.registerForm.phone,
          password: this.registerForm.password
        }
        const response = await registerUser(payload)
        if (response.data.code !== 200) {
          ElMessage.error(this.localizeAuthError(response.data.msg, 'Unable to create the account. Review the details and try again.'))
          return
        }
        this.finishAuth(response.data, 'user')
      } catch (error) {
        const message = getApiErrorMessage(error, 'Unable to reach the service. Please try again.')
        ElMessage.error(this.localizeAuthError(message, 'Unable to reach the service. Please try again.'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.auth-page {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;
  background-image: url("../images/login-agent-field-v2-16x9.jpg") !important;
  background-position: center 48% !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  color: #ffffff;
}

.scene-shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: rgba(5, 12, 18, 0.48);
  box-shadow: inset 0 0 180px rgba(0, 0, 0, 0.54);
}

.auth-shell {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 410px);
  gap: clamp(44px, 8vw, 140px);
  align-items: center;
  width: min(1500px, 100%);
  margin: 0 auto;
  padding: clamp(30px, 5vw, 76px);
}

.auth-copy {
  position: relative;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 20px 0;
  border: 0 !important;
  background: none !important;
  box-shadow: none !important;
  text-shadow: 0 2px 28px rgba(0, 0, 0, 0.48);
}

.brand-lockup {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-mark-image {
  display: block;
  width: 42px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
  background: #fdf8ed;
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.2);
}

.brand-lockup strong,
.brand-lockup small {
  display: block;
}

.brand-lockup strong {
  font-size: 16px;
  letter-spacing: 0;
}

.brand-lockup small {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 9px;
  letter-spacing: 0.08em;
}

.eyebrow {
  margin: 0 0 16px;
  font-size: 12px;
  letter-spacing: 0.16em;
  color: #d7f5ff;
  text-transform: uppercase;
  font-weight: 800;
}

.auth-copy h1 {
  margin: 0;
  max-width: 800px;
  font-size: 64px;
  line-height: 1.08;
  font-family: "Noto Sans", system-ui, sans-serif;
  color: #ffffff;
  font-weight: 760;
  letter-spacing: 0;
}

.lead {
  margin: 22px 0 0;
  max-width: 640px;
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
}

.ecosystem-prompt {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  color: rgba(255, 255, 255, 0.86);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}

.live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #70e5aa;
  box-shadow: 0 0 0 5px rgba(112, 229, 170, 0.16);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.mobile-access {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  width: min(100%, 410px);
  margin-top: 20px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: rgba(7, 17, 23, 0.48);
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(12px);
  text-shadow: none;
}

.mobile-qr-box {
  display: grid;
  place-items: center;
  width: 112px;
  height: 112px;
  padding: 6px;
  border-radius: 6px;
  color: #5f6b71;
  background: #ffffff;
}

.mobile-qr-box img {
  display: block;
  width: 100%;
  height: 100%;
}

.mobile-qr-box .el-icon {
  font-size: 24px;
  animation: qrLoading 1.1s linear infinite;
}

.mobile-access-copy > span {
  color: #9ee8d1;
  font-size: 9px;
  font-weight: 850;
  text-transform: uppercase;
}

.mobile-access-copy h2 {
  margin: 4px 0 0;
  color: #ffffff;
  font-size: 15px;
  line-height: 1.35;
  letter-spacing: 0;
}

.mobile-access-copy p {
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.62);
  font-size: 10px;
  line-height: 1.45;
}

.mobile-access-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 9px;
}

.mobile-access-actions button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 30px;
  padding: 0 9px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 6px;
  color: #ffffff;
  font-size: 10px;
  font-weight: 750;
  background: rgba(255, 255, 255, 0.08);
}

.mobile-access-actions button:hover,
.mobile-access-actions button:focus-visible {
  border-color: rgba(255, 255, 255, 0.62);
  background: rgba(255, 255, 255, 0.16);
  outline: none;
}

@keyframes qrLoading {
  to {
    transform: rotate(360deg);
  }
}

.hero-btn {
  min-height: 42px;
  padding: 0 17px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 750;
  letter-spacing: 0;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.hero-btn:hover,
.hero-btn:focus-visible {
  transform: translateY(-2px);
  outline: none;
}

.hero-btn-primary {
  border: 1px solid #ffffff;
  background: #ffffff;
  color: #101820;
}

.hero-btn-primary:hover,
.hero-btn-primary:focus-visible {
  background: #e8f8ef;
  border-color: #e8f8ef;
}

.hero-btn-secondary {
  border: 1px solid rgba(255, 255, 255, 0.58);
  background: rgba(7, 17, 23, 0.28);
  color: #ffffff;
  backdrop-filter: blur(8px);
}

.hero-btn-secondary:hover,
.hero-btn-secondary:focus-visible {
  background: rgba(7, 17, 23, 0.52);
  border-color: #ffffff;
}

.auth-panel {
  box-sizing: border-box;
  width: 100%;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px 28px 28px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 8px;
  background: rgba(251, 252, 253, 0.96) !important;
  color: #111820;
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(16px);
}

.panel-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 22px;
}

.panel-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #111820;
  font-size: 15px;
  font-weight: 800;
}

.panel-brand-image {
  display: block;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  object-fit: cover;
  background: #fdf8ed;
}

.panel-heading {
  margin-bottom: 17px;
}

.panel-heading p {
  margin: 0 0 7px;
  color: #64717c;
  font-size: 13px;
}

.panel-heading h2 {
  margin: 0;
  color: #101820;
  font-size: 24px;
  line-height: 1.25;
  letter-spacing: 0;
}

.home-link {
  padding: 7px 0;
  border: none !important;
  background: transparent !important;
  color: #53616c !important;
  font-size: 12px;
  cursor: pointer;
}

.auth-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  margin: 0 0 18px;
  border-radius: 8px;
  background: #edf1f3;
  border: 1px solid #e0e6e9;
}

.auth-switch button {
  height: 36px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #65727d;
  cursor: pointer;
  font-weight: 700;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.auth-switch button.active {
  color: #101820;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(25, 38, 48, 0.1);
}

.auth-switch button:hover {
  color: #101820;
}

.auth-form-wrap {
  min-height: 238px;
}

.auth-form {
  position: relative;
  z-index: 2;
}

.phone-grid {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 14px;
}

.auth-form :deep(.el-form-item__label) {
  color: #34414b !important;
  padding-bottom: 6px;
  font-size: 13px;
  font-weight: 700;
}

.auth-form :deep(.el-input__wrapper) {
  position: relative;
  z-index: 2;
  min-height: 42px;
  background: #f6f8f9 !important;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dfe5e8 inset !important;
  pointer-events: auto;
  cursor: text;
}

.auth-form :deep(.el-input__wrapper.is-focus) {
  background: #ffffff !important;
  box-shadow: 0 0 0 1px #1581a2 inset, 0 0 0 3px rgba(21, 129, 162, 0.12) !important;
}

.auth-form :deep(.el-input__inner) {
  position: relative;
  z-index: 3;
  color: #101820 !important;
  -webkit-text-fill-color: #101820;
  caret-color: #117a98;
  pointer-events: auto;
  cursor: text;
  user-select: text;
}

.auth-form :deep(.el-input__inner::placeholder) {
  color: #8a959d !important;
  -webkit-text-fill-color: #8a959d;
}

.auth-form :deep(.el-input__password) {
  color: #586771;
  cursor: pointer;
}

.password-control {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px;
  align-items: center;
  width: 100%;
  min-height: 42px;
  border-radius: 8px;
  background: #f6f8f9;
  box-shadow: 0 0 0 1px #dfe5e8 inset;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.password-control:focus-within {
  background: #ffffff;
  box-shadow: 0 0 0 1px #1581a2 inset, 0 0 0 3px rgba(21, 129, 162, 0.12);
}

.password-control input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 0 0 12px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #101820;
  -webkit-text-fill-color: #101820;
  caret-color: #117a98;
}

.password-control input::placeholder {
  color: #8a959d;
  -webkit-text-fill-color: #8a959d;
}

.password-control button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #586771;
  cursor: pointer;
}

.password-control button:hover,
.password-control button:focus-visible {
  background: #e7ecef;
  color: #101820;
  outline: none;
}

.field-tip {
  margin: -4px 0 18px;
  color: #71808a;
  line-height: 1.65;
  font-size: 12px;
}

.primary-btn {
  width: 100%;
  height: 44px;
  margin-top: 6px;
  border: 1px solid #101820;
  border-radius: 8px;
  background: #101820 !important;
  color: #ffffff !important;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 14px 28px rgba(16, 24, 32, 0.18) !important;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.warm-btn {
  background: #146f89 !important;
  border-color: #146f89;
}

.primary-btn:hover,
.home-link:hover {
  transform: translateY(-1px);
}

.primary-btn:hover {
  background: #23313b !important;
}

.primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.reveal-up {
  animation: revealUp 0.85s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.delay-1 {
  animation-delay: 0.12s;
}

@keyframes revealUp {
  from {
    opacity: 0;
    transform: translateY(22px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1080px) {
  .auth-shell {
    grid-template-columns: minmax(0, 1fr) minmax(350px, 390px);
    gap: 34px;
    padding: 30px;
  }

  .brand-lockup {
    top: 0;
    left: 0;
  }

  .auth-copy h1 {
    font-size: 50px;
  }
}

@media (max-width: 760px) {
  .auth-page {
    background-position: 55% center !important;
  }

  .scene-shade {
    background: rgba(4, 10, 15, 0.58);
  }

  .auth-shell {
    grid-template-columns: 1fr;
    gap: 26px;
    padding: 92px 18px 28px;
  }

  .brand-lockup {
    top: 18px;
    left: 2px;
  }

  .auth-copy {
    align-self: auto;
    padding: 82px 2px 0;
  }

  .auth-copy h1 {
    font-size: 38px;
    line-height: 1.12;
  }

  .lead {
    margin-top: 16px;
    font-size: 15px;
  }

  .ecosystem-prompt {
    margin-top: 22px;
  }

  .hero-actions {
    display: grid;
    grid-template-columns: 1fr;
    max-width: 340px;
  }

  .mobile-access {
    display: none;
  }

  .auth-panel {
    width: min(100%, 410px);
    justify-self: center;
    max-height: none;
    border-radius: 8px;
    padding: 22px 20px 26px;
  }

  .phone-grid {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal-up {
    animation: none;
  }
}
</style>
